{
  "$schema": "https://cogo.xyz/ontology/v1/relationship-rules",
  "version": "1.0.0",
  "description": "Cross-file relationship validation rules for Cogo JSON blocks. Every declarative relationship MUST be machine-verifiable — Cogo JSON's core strength is 100% static analyzability.",

  "stage9_dataBindingIntegrity": {
    "description": "Validates that UI data bindings match the actual data schema in variables",
    "rules": [
      {
        "id": "data-table-column-keys",
        "description": "Every data-table column.key must exist as a field in the bound data source's initial schema",
        "source": "ui.cogo.json → data-table.columns[].key",
        "target": "variables.cogo.json → state.{dataPath}.default[] → object keys",
        "level": "warning",
        "resolution": "Add missing field to initial data OR fix column key to match actual field name"
      },
      {
        "id": "data-table-data-source-exists",
        "description": "data-table.data must reference a declared variable path",
        "source": "ui.cogo.json → data-table.data (#{xxx})",
        "target": "variables.cogo.json → state path",
        "level": "warning",
        "resolution": "Declare the data array variable in variables.cogo.json"
      },
      {
        "id": "input-bind-path-exists",
        "description": "Every input/select bind path must resolve to a declared variable",
        "source": "ui.cogo.json → input.bind / select.bind",
        "target": "variables.cogo.json → state path",
        "level": "warning",
        "resolution": "Declare the bound variable in variables.cogo.json state"
      },
      {
        "id": "iterateOver-source-is-array",
        "description": "iterateOver source must reference a variable that defaults to an array",
        "source": "ui.cogo.json → element.iterateOver (#{xxx})",
        "target": "variables.cogo.json → state path → default must be array",
        "level": "info",
        "resolution": "Ensure the variable default is an array []"
      }
    ]
  },

  "stage10_eventFlowContract": {
    "description": "Validates the contract between event bindings and flow definitions",
    "rules": [
      {
        "id": "event-params-match-flow-refs",
        "description": "Every #{event.xxx} reference in a flow must have a corresponding param in the event binding that triggers it",
        "source": "flow.cogo.json → flows.{name}.steps → #{event.xxx}",
        "target": "events.cogo.json → bindings.{elementId:eventType}.params.xxx",
        "level": "warning",
        "resolution": "Add the missing param to the event binding, or fix the flow reference"
      },
      {
        "id": "event-flow-exists",
        "description": "Event binding flow reference must exist (already in Stage 8, but restated for completeness)",
        "source": "events.cogo.json → bindings.{key}.flow",
        "target": "flow.cogo.json → flows.{name}",
        "level": "error",
        "resolution": "Create the referenced flow or fix the flow name"
      },
      {
        "id": "unused-event-params",
        "description": "Event binding params that are never referenced by #{event.xxx} in the target flow are dead code",
        "source": "events.cogo.json → bindings.{key}.params.xxx",
        "target": "flow.cogo.json → flows.{name} → #{event.xxx}",
        "level": "info",
        "resolution": "Remove unused params or add flow logic that uses them"
      }
    ]
  },

  "stage11_flowResultChain": {
    "description": "Validates that flow step result chains are consistent — resultTarget writes match subsequent reads",
    "rules": [
      {
        "id": "result-target-consumed",
        "description": "A resultTarget written by one step should be consumed by a subsequent step in the same flow",
        "source": "flow.cogo.json → step.params.resultTarget",
        "target": "flow.cogo.json → subsequent steps → #{_results.xxx}",
        "level": "info",
        "resolution": "Use the result in a subsequent step or remove the resultTarget"
      },
      {
        "id": "result-ref-has-writer",
        "description": "Every #{_results.xxx} reference must have a preceding step that writes to that resultTarget",
        "source": "flow.cogo.json → step → #{_results.xxx.yyy}",
        "target": "flow.cogo.json → preceding step.params.resultTarget = #_results.xxx",
        "level": "warning",
        "resolution": "Add a step that writes to the referenced result path, or fix the reference"
      },
      {
        "id": "setState-from-result-path-depth",
        "description": "When setState uses a result reference, the path depth should match the result structure (e.g., dateRange.from not dateRange alone when result is an object)",
        "source": "flow.cogo.json → setState.value → #{_results.xxx.data}",
        "target": "flow.cogo.json → expression step return structure",
        "level": "warning",
        "resolution": "Access specific fields of the result object (e.g., .from, .to) instead of the whole object"
      }
    ]
  },

  "layoutConstraints": {
    "description": "UI layout pattern rules — detects responsive and overflow issues from className patterns",
    "rules": [
      {
        "id": "flex-row-no-responsive",
        "description": "Horizontal flex layout with multiple items should have responsive breakpoint classes for mobile adaptation",
        "detect": {
          "horizontalPatterns": ["flex", "inline-flex"],
          "excludePatterns": ["flex-col", "flex-column", "flex-wrap", "overflow-x-", "grid-cols-"],
          "minChildren": 3
        },
        "require": {
          "responsivePrefixes": ["sm:", "md:", "lg:", "xl:", "2xl:"]
        },
        "excludeIds": ["nav", "navbar", "footer", "header", "menu-bar", "tab-bar", "breadcrumb"],
        "level": "warning",
        "resolution": "Add responsive classes: flex-col sm:flex-row, or grid-cols-1 sm:grid-cols-3"
      },
      {
        "id": "flex-row-overflow",
        "description": "Horizontal flex row with many children risks overflow without wrap or scroll",
        "detect": {
          "horizontalPatterns": ["flex", "inline-flex"],
          "excludePatterns": ["flex-col", "flex-column", "flex-wrap", "overflow-x-", "overflow-auto", "overflow-scroll", "grid-cols-"],
          "minChildren": 5
        },
        "require": {
          "anyOf": ["flex-wrap", "overflow-x-auto", "overflow-x-scroll"]
        },
        "level": "warning",
        "resolution": "Add flex-wrap or overflow-x-auto to prevent content overflow"
      },
      {
        "id": "iterate-fixed-cols",
        "description": "iterateOver containers should use grid for predictable responsive layout",
        "detect": {
          "hasIterateOver": true,
          "horizontalPatterns": ["flex"],
          "excludePatterns": ["flex-col", "flex-wrap", "grid"]
        },
        "require": {
          "anyOf": ["grid", "flex-wrap"]
        },
        "level": "info",
        "resolution": "Use grid grid-cols-N with responsive breakpoints for iterateOver containers"
      }
    ]
  },

  "colorContrast": {
    "description": "Detects text color classes that are known to have low contrast in light/dark themes",
    "rules": [
      {
        "id": "low-contrast-text-accent",
        "description": "text-accent and text-secondary are near-white in default light theme — invisible on light backgrounds",
        "dangerousClasses": ["text-accent", "text-secondary/50", "text-secondary/40", "text-secondary/30", "text-muted/50", "text-muted/40"],
        "targetTypes": ["badge", "text", "button"],
        "excludeParentBg": ["bg-primary", "bg-foreground", "bg-black", "bg-zinc-900", "bg-slate-900", "bg-gray-900", "dark:"],
        "level": "warning",
        "resolution": "Use text-primary, text-foreground, or a high-contrast color instead of text-accent/text-secondary on light backgrounds"
      }
    ]
  }
}
